import * as React from 'react';
import { useTranslations } from '../../../store/translations/hooks';
import { AutocompleteTextField, ComboField, TextInputField, NumberInputField, TimeField } from '../../../components/Forms/fields';
import { TK } from '../../../store/translations/translationKeys';
import { SettingsItemContentProps } from '../SettingsItem';
import { BranchSettings, FeatureEnum, GeneralSettingsData, RoomSettings } from '../../../models';
import { Form, Table, ButtonGroup, Button, ListGroup, ListGroupItem, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { asSnake } from '../../../shared/string';
import { DynamicRecordField } from '../../../components/Forms/fields/DynamicRecordField';
import CollapsableItem from '../../../components/CollapsableItem';
import ToolTip from '../../../components/ToolTip';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import ColorBall from '../../../components/ColorBall';
import { useSelector, useDispatch } from 'react-redux';
import { getFeatures } from '../../../store/session/selectors';
import { FC, useState } from 'react';
import { AvailabilityField } from '../../../components/Forms/ProfessionalForm/AvailabilityField';
import { DateIntervalField } from '../../../components/Forms/fields/DateIntervalField';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { ClientsIntelligentCategorizationSettingsLink } from '../../../components/ClientsIntelligentCategorizationSettingsLink';
import { uploadCompanyImageAsync } from '../../../ajax/requests';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { CircularProgress } from '@mui/material';
import styled from 'styled-components';
import { isMultiBranchGoogleReviewSetup } from '../../../shared/googleReviewBranches';

const TopSection = styled.div`
    display: flex;
    gap: var(--spacer-M);
    align-items: flex-start;

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const LogoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 140px;
    align-self: flex-start;

    @media (max-width: 600px) {
        width: 100%;
    }
`;

const LogoFieldsWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
    min-width: 0;
`;

const LogoBox = styled.div<{ hasImage?: boolean }>`
    position: relative;
    width: 100%;
    min-height: 100px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: var(--spacer-XS);
    border: ${({ hasImage }) => hasImage
        ? `var(--border-thin) solid var(--appLigthGrey)`
        : `var(--border-medium) dashed var(--appLigthGrey)`};
    background: var(--bg-light);
    color: var(--text-placeholder);
    text-align: center;
    font-size: 0.78rem;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: var(--appBlue);
    }
`;

const LogoImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
`;

const LogoOverlay = styled.div<{ show?: boolean }>`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacer-XS);
    background: rgba(15, 23, 42, 0.65);
    color: white;
    opacity: ${({ show }) => show ? 1 : 0};
    transition: opacity 0.2s ease;
    border-radius: 8px;

    ${LogoBox}:hover & {
        opacity: 1;
    }
`;

const LogoActionButton = styled.button`
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;
    color: white;
    padding: 0;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.35);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    svg {
        font-size: 1.1rem;
    }
`;

interface CompanyLogoUploaderProps {
    label: string;
    value?: string;
    editLabel: string;
    removeLabel: string;
    onUpload: (file: File) => Promise<void>;
    onRemove: () => void;
}

const CompanyLogoUploader: FC<CompanyLogoUploaderProps> = ({ label, value, editLabel, removeLabel, onUpload, onRemove }) => {
    const dispatch = useDispatch();
    const t = useTranslations();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const onlyImageFilesAllowedMessage = t(TK.onlyImageFilesAllowed);
    const fileSizeMustBeLessThan2MBMessage = t(TK.fileSizeMustBeLessThan2MB);
    const profilePictureUpdatedMessage = t(TK.profilePictureUpdated);
    const errorUploadingProfilePictureMessage = t(TK.errorUploadingProfilePicture);

    const handleClick = () => {
        if (!isUploading) fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            dispatch(alertError(onlyImageFilesAllowedMessage));
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            dispatch(alertError(fileSizeMustBeLessThan2MBMessage));
            return;
        }

        setIsUploading(true);
        try {
            await onUpload(file);
            dispatch(alertSuccess(profilePictureUpdatedMessage));
        } catch {
            dispatch(alertError(errorUploadingProfilePictureMessage));
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove();
    };

    return (
        <LogoWrapper>
            <LogoBox hasImage={!!value} onClick={!value ? handleClick : undefined}>
                {value ? (
                    <>
                        <LogoImage src={value} alt={label} />
                        <LogoOverlay show={isUploading}>
                            {isUploading ? (
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : (
                                <>
                                    <LogoActionButton type="button" onClick={handleClick} title={editLabel}>
                                        <EditIcon fontSize="small" />
                                    </LogoActionButton>
                                    <LogoActionButton type="button" onClick={handleRemove} title={removeLabel}>
                                        <DeleteIcon fontSize="small" />
                                    </LogoActionButton>
                                </>
                            )}
                        </LogoOverlay>
                    </>
                ) : isUploading ? (
                    <CircularProgress size={24} />
                ) : (
                    <>
                        <AddPhotoAlternateIcon style={{ fontSize: '2rem', color: 'var(--appLigthGrey)' }} />
                        <span>{label}</span>
                    </>
                )}
            </LogoBox>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        </LogoWrapper>
    );
};

const defaultRoomSettings: RoomSettings = Object.freeze<RoomSettings>({
    maxCapacity: 1,
    timeAvailability: [],
})

export interface GeneralSettingsProps extends SettingsItemContentProps {
    values: GeneralSettingsData,
    onChange: (settings: GeneralSettingsData) => void
}

export const resolveSelectedCountryCode = (countryCode: string | undefined, currentCountryCode: string | undefined): string => {
    return countryCode || currentCountryCode || 'pt-PT';
}

const RoomsSettings: FC<{
    id?: string,
    values: Record<string, RoomSettings>,
    onChange: (values: Record<string, RoomSettings>) => void,
    allowAppointmentsWithoutRoom?: boolean,
    onAllowAppointmentsWithoutRoomChange: (value?: boolean) => void,
}> = ({
    id,
    values,
    onChange,
    allowAppointmentsWithoutRoom,
    onAllowAppointmentsWithoutRoomChange,
}) => {
    const t = useTranslations()
    const [editingRoom, setEditingRoom] = useState<string | undefined>(undefined)
    const [newRoomName, setNewRoomName] = useState('')

    const handleAddRoom = () => {
        const name = newRoomName.trim();
        if (!name || name in values) return;
        onChange({ ...values, [name]: { ...defaultRoomSettings } });
        setNewRoomName('');
    };

    const handleUpdateAvailability = (room: string, timeAvailability: RoomSettings['timeAvailability']) => {
        onChange({
            ...values,
            [room]: {...values[room], timeAvailability}
        })
    }

    return (<div>
        <AutocompleteTextField
            id={`${id ?? 'input'}-set-rooms`}
            label={t(TK.rooms)}
            helpText={t(TK.roomsHelpNote)}
            multiSelect
            options={Object.keys(values ?? {})}
            values={Object.keys(values ?? {})}
            onAddNewOption={value => onChange({...values, [value]: {...defaultRoomSettings}})}
            onValuesChanged={rooms => onChange(Object.fromEntries([
                ...Object.entries(values).filter(([room]) => rooms.includes(room)), // Remove
                ...rooms.filter(r => !(r in values)).map(r => [r, {...defaultRoomSettings}]), // Add news
            ]))}
        />

        <CollapsableItem title={t(TK.advanced)} size='sm' style={{paddingLeft: '1rem'}}>
            <div style={{paddingBottom: '2rem'}}>
                <CheckInputField
                    id={'allow-appointments-without-room'}
                    label={t(TK.allowAppointmentsWithoutRoom)}
                    value={allowAppointmentsWithoutRoom}
                    onChange={onAllowAppointmentsWithoutRoomChange}
                />
                <Table responsive size="sm">
                    <thead>
                        <th></th>
                        <th id='room-capacity-header'>{t(TK.capacity)}</th>
                        <th>
                            {t(TK.availability)}
                            <InfoIcon
                                id='room-availability-header'
                                style={{
                                    color: 'var(--info-color)',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    marginLeft: 'var(--spacer-XXS)',
                                    verticalAlign: 'middle'
                                }}
                            />
                        </th>
                    </thead>
                    <tbody>
                        {Object.entries(values).map(([room, roomSettings]) => (
                            <tr key={room}>
                                <td>{room}</td>
                                <td>
                                    <TextInputField
                                        type='number'
                                        id={`${id}-${asSnake(room)}-max-capacity`}
                                        value={roomSettings.maxCapacity}
                                        onChange={value => onChange({
                                            ...values,
                                            [room]: {...roomSettings, maxCapacity: Number(value) ?? 1}
                                        })}
                                    />
                                </td>
                                <td>
                                    <Button
                                        id={`${id}-${asSnake(room)}-edit-availability`}
                                        color="link"
                                        size="sm"
                                        onClick={() => setEditingRoom(room)}
                                    >
                                        <EditIcon style={{width: '1rem'}} />
                                        {roomSettings.timeAvailability?.length ? ` (${roomSettings.timeAvailability.length})` : ''}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td>
                                <input
                                    className="form-control form-control-sm"
                                    placeholder={t(TK.name)}
                                    value={newRoomName}
                                    onChange={e => setNewRoomName(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleAddRoom()}
                                />
                            </td>
                            <td colSpan={2}>
                                <Button
                                    color="link"
                                    size="sm"
                                    disabled={!newRoomName.trim() || newRoomName.trim() in values}
                                    onClick={handleAddRoom}
                                >
                                    <AddIcon style={{width: '1rem'}} />
                                    {t(TK.add)}
                                </Button>
                            </td>
                        </tr>
                    </tbody>
                </Table>
                <ToolTip target={'room-capacity-header'} toolTipContent={t(TK.roomCapacityToolTip)}/>
                <ToolTip target={'room-availability-header'} toolTipContent={t(TK.roomAvailabilityToolTip)}/>
            </div>
        </CollapsableItem>

        {editingRoom && (
            <Modal isOpen={true} toggle={() => setEditingRoom(undefined)} size="lg">
                <ModalHeader toggle={() => setEditingRoom(undefined)}>
                    {editingRoom} - {t(TK.availability)}
                </ModalHeader>
                <ModalBody>
                    <AvailabilityField
                        id={`${id}-${asSnake(editingRoom)}-availability`}
                        values={values[editingRoom]?.timeAvailability ?? []}
                        onChange={(timeAvailability) => handleUpdateAvailability(editingRoom, timeAvailability)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setEditingRoom(undefined)}>
                        {t(TK.close)}
                    </Button>
                </ModalFooter>
            </Modal>
        )}
    </div>)
}

const GeneralSettings: React.FC<GeneralSettingsProps> = (props) => {
    const {
        id,
        values,
        onChange,
    } = props;

    const t = useTranslations();
    const features = useSelector(getFeatures)
    const hasBranches = features.includes(FeatureEnum.Branches) && Object.keys(values.branches ?? {}).length > 0;
    const isMultiBranch = features.includes(FeatureEnum.Branches) && isMultiBranchGoogleReviewSetup(values.branches);
    const normalizedCountryCode = values.countryCode || 'pt-PT';
    const countryOptions = React.useMemo(() => [
        { value: 'pt-PT', label: `🇵🇹 ${t(TK.portugal)}` },
        { value: 'fr-FR', label: `🇫🇷 ${t(TK.france)}` },
        { value: 'en-US', label: `🇺🇸 ${t(TK.other)}` },
    ], [t]);

    return (
        <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
            <TopSection>
                <CompanyLogoUploader
                    label={t(TK.companyImage)}
                    value={values.companyImageUrl}
                    editLabel={t(TK.edit)}
                    removeLabel={t(TK.remove)}
                    onUpload={async (file) => {
                        const result = await uploadCompanyImageAsync(file);
                        onChange({ ...values, companyImageUrl: result.url });
                    }}
                    onRemove={() => onChange({ ...values, companyImageUrl: undefined })}
                />
                <LogoFieldsWrapper>
                    <Row>
                        <Col md={8}>
                            <TextInputField
                                id={`${id}-company-name`}
                                label={t(TK.companyName)}
                                value={values.companyName}
                                type="text"
                                onChange={companyName => onChange({...values, companyName})} />
                        </Col>
                        <Col md={4}>
                            <ComboField
                                id={`${id}-country-code`}
                                label={t(TK.country)}
                                value={normalizedCountryCode}
                                options={countryOptions}
                                onChange={countryCode => onChange({ ...values, countryCode: resolveSelectedCountryCode(countryCode, values.countryCode) })}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <TimeField
                                id={`${id}-openning-hour`}
                                label={t(TK.openingHour)}
                                value={values.openningHour}
                                onChange={openningHour => onChange({ ...values, openningHour })}/>
                        </Col>
                        <Col md={4}>
                            <TimeField
                                id={`${id}-closing-hour`}
                                label={t(TK.closingHour)}
                                value={values.closingHour}
                                onChange={closingHour => onChange({ ...values, closingHour })}/>
                        </Col>
                    </Row>
                </LogoFieldsWrapper>
            </TopSection>

            {!isMultiBranch && (
                <TextInputField
                    id={`${id}-google-review-url`}
                    label={t(TK.googleReviewUrl)}
                    helpText={t(TK.googleReviewUrlHelp)}
                    value={values.googleReviewUrl}
                    type="url"
                    onChange={googleReviewUrl => onChange({ ...values, googleReviewUrl: googleReviewUrl || undefined })}
                />
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                <CollapsableItem title={t(TK.holidayPeriods)} size='sm'>
                    <DateIntervalField
                        id={`${id}-absences`}
                        values={values.absences ?? []}
                        onChange={absences => onChange({ ...values, absences })}
                        enableRecurring={true}
                        addViaModal={true}
                        addModalTitle={t(TK.holidayPeriods)}
                    />
                </CollapsableItem>

                <CollapsableItem title={t(TK.contacts)} size='sm'>
                    <DynamicRecordField
                        value={values.contacts}
                        label={t(TK.contactInformation)}
                        labelKey={t(TK.descriptive)}
                        labelValue={t(TK.contact)}
                        addButtonPosition='bottom'
                        onChange={contacts => onChange({ ...values, contacts })}
                    />
                </CollapsableItem>

                <CollapsableItem title={t(TK.advancedConfigurations)} size='sm'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                    <CheckInputField
                        id={'do-not-use-appointments-confirmation'}
                        label={t(TK.doNotUseAppointmentsConfirmation)}
                        value={values.doNotUseAppointmentConfirmation}
                        onChange={doNotUseAppointmentConfirmation => onChange({...values, doNotUseAppointmentConfirmation})}/>
                    <CheckInputField
                        id={'disable-missing-payment-notifications'}
                        label={t(TK.disableMissingPaymentNotifications)}
                        value={values.disableMissingPaymentNotifications}
                        onChange={disableMissingPaymentNotifications => onChange({...values, disableMissingPaymentNotifications})}/>
                    <CheckInputField
                        id={'use-client-file-number'}
                        label={t(TK.useClientFileNumber)}
                        helpText={t(TK.useClientFileNumberTooltip)}
                        value={values.useClientFileNumber}
                        onChange={useClientFileNumber => onChange({...values, useClientFileNumber})}/>
                    <div>
                        <label style={{display: 'block', marginBottom: '0.5rem'}}>{t(TK.professionalCanEditAppointmentUpTo)}</label>
                        <div style={{display: 'flex', gap: 'var(--spacer-XS)'}}>
                            <div style={{maxWidth: '10rem'}}>
                                <NumberInputField
                                    id={`${id}-professional-can-edit-appointments-hours`}
                                    append="h"
                                    value={Math.floor((Math.abs(values.professionalCanEditAppointmentUpToMinutes ?? 0))/60)}
                                    min={0}
                                    onChange={hours => {
                                        const curr = values.professionalCanEditAppointmentUpToMinutes ?? -0
                                        onChange({
                                            ...values,
                                            professionalCanEditAppointmentUpToMinutes: (curr <= 0 ? -1 : 1) * ((hours ?? 0)*60 + (Math.abs(curr)%60)),
                                        })
                                    }}
                                />
                            </div>
                            <div style={{maxWidth: '10rem'}}>
                                <NumberInputField
                                    id={`${id}-professional-can-edit-appointments-minutes`}
                                    append="m"
                                    value={Math.abs(values.professionalCanEditAppointmentUpToMinutes ?? 0)%60}
                                    min={0}
                                    onChange={minutes => {
                                        const curr = values.professionalCanEditAppointmentUpToMinutes ?? -0
                                        onChange({
                                            ...values,
                                            professionalCanEditAppointmentUpToMinutes: (curr <= 0 ? -1 : 1) * Math.floor(Math.abs(curr)/60)*60 + (minutes ?? 0),
                                        })
                                    }}
                                />
                            </div>
                            <ButtonGroup>
                                <Button
                                    id={`${id}-professional-can-edit-appointment-before-button`}
                                    active={Math.sign(values.professionalCanEditAppointmentUpToMinutes ?? 0) <= 0}
                                    onClick={() => onChange({
                                        ...values,
                                        professionalCanEditAppointmentUpToMinutes: -1 * Math.abs(values.professionalCanEditAppointmentUpToMinutes ?? 0)}
                                    )}
                                >
                                    {t(TK.before)}
                                </Button>
                                <Button
                                    id={`${id}-professional-can-edit-appointment-after-button"`}
                                    active={Math.sign(values.professionalCanEditAppointmentUpToMinutes ?? 0) > 0}
                                    onClick={() => onChange({
                                        ...values,
                                        professionalCanEditAppointmentUpToMinutes: Math.abs(values.professionalCanEditAppointmentUpToMinutes ?? 0)}
                                    )}
                                >
                                    {t(TK.after)}
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>
                </div>
                </CollapsableItem>
            </div>

            {features.includes(FeatureEnum.Branches) && <div>
                <AutocompleteTextField
                    id={`${id}-set-branches`}
                    label={t(TK.branches)}
                    helpText={t(TK.branchesHelpNote)}
                    multiSelect
                    options={Object.keys(values.branches ?? {})}
                    values={Object.keys(values.branches ?? {})}
                    onAddNewOption={value => onChange({...values, branches: {...values.branches, [value]: {displayName: value, rooms: {}, allowAppointmentsWithoutRoom: values.allowAppointmentsWithoutRoom}}})}
                    onValuesChanged={branches => onChange({
                        ...values,
                        branches: Object.fromEntries([
                            ...Object.entries(values.branches ?? {}).filter(([branch]) => branches.includes(branch)), // Remove
                            ...branches.filter(s => !(s in (values.branches ?? {}))).map<[string, BranchSettings]>(s => [s, {displayName: s, rooms: {}, allowAppointmentsWithoutRoom: values.allowAppointmentsWithoutRoom}]), // Add news
                        ]),
                    })}
                />

                <CollapsableItem title={t(TK.advanced)} size='sm' style={{paddingLeft: '1rem'}}>  
                    <ListGroup>
                        {Object.entries(values.branches ?? {}).map(([id, branchConfig]) => (
                            <ListGroupItem key={id} style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                                <TextInputField 
                                    label={t(TK.name)}
                                    value={branchConfig.displayName}
                                    onChange={displayName => onChange({
                                        ...values,
                                        branches: Object.fromEntries(Object.entries(values.branches ?? {}).map(entry => entry[0] !== id ? entry : [
                                            id,
                                            {
                                                ...entry[1],
                                                displayName,
                                            }
                                        ]))
                                    })}
                                />
                                <TextInputField
                                    label={t(TK.googleReviewBranchUrlLabel)}
                                    helpText={t(TK.googleReviewBranchUrlHelp)}
                                    value={branchConfig.googleReviewUrl ?? ''}
                                    type="url"
                                    onChange={googleReviewUrl => onChange({
                                        ...values,
                                        branches: Object.fromEntries(Object.entries(values.branches ?? {}).map(entry => entry[0] !== id ? entry : [
                                            id,
                                            {
                                                ...entry[1],
                                                googleReviewUrl: googleReviewUrl || undefined,
                                            }
                                        ]))
                                    })}
                                />
                                <RoomsSettings
                                    values={branchConfig.rooms ?? {}}
                                    onChange={rooms => onChange({
                                        ...values,
                                        branches: Object.fromEntries(Object.entries(values.branches ?? {}).map(entry => entry[0] !== id ? entry : [
                                            id,
                                            {
                                                ...entry[1],
                                                rooms,
                                            }
                                        ]))
                                    })}
                                    allowAppointmentsWithoutRoom={branchConfig.allowAppointmentsWithoutRoom}
                                    onAllowAppointmentsWithoutRoomChange={allowAppointmentsWithoutRoom => onChange({
                                        ...values,
                                        branches: Object.fromEntries(Object.entries(values.branches ?? {}).map(entry => entry[0] !== id ? entry : [
                                            id,
                                            {
                                                ...entry[1],
                                                allowAppointmentsWithoutRoom,
                                            }
                                        ]))
                                    })}
                                />
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </CollapsableItem>
            </div>}

            {!hasBranches && (
                <RoomsSettings
                    id={id}
                    values={values.rooms}
                    onChange={rooms => onChange({...values, rooms})}
                    allowAppointmentsWithoutRoom={values.allowAppointmentsWithoutRoom}
                    onAllowAppointmentsWithoutRoomChange={allowAppointmentsWithoutRoom => onChange({
                        ...values,
                        allowAppointmentsWithoutRoom,
                        branches: Object.fromEntries(Object.entries(values.branches ?? {}).map(([key, branch]) => [
                            key,
                            {...branch, allowAppointmentsWithoutRoom},
                        ])),
                    })}
                />
            )}

            <AutocompleteTextField
                id={`${id}-set-payment-types`}
                label={t(TK.paymentTypes)}
                helpText={t(TK.paymentTypesHelpNote)}
                multiSelect
                options={values.paymentMethods}
                values={values.paymentMethods}
                onAddNewOption={value => onChange({...values, paymentMethods: [...values.paymentMethods, value]})}
                onValuesChanged={paymentMethods => onChange({...values, paymentMethods})}
            />

            <AutocompleteTextField
                id={`${id}-set-client-origins`}
                label={t(TK.origins)}
                helpText={t(TK.originsHelpNote)}
                multiSelect
                options={values.clientOrigins}
                values={values.clientOrigins}
                onAddNewOption={value => onChange({...values, clientOrigins: [...values.clientOrigins, value]})}
                onValuesChanged={clientOrigins => onChange({...values, clientOrigins})}
            />

            <AutocompleteTextField
                id={`${id}-set-expense-types`}
                label={t(TK.expenseTypes)}
                multiSelect
                options={values.expenseTypes}
                values={values.expenseTypes}
                onAddNewOption={value => onChange({...values, expenseTypes: [...values.expenseTypes, value]})}
                onValuesChanged={expenseTypes => onChange({...values, expenseTypes})}
            />

            <AutocompleteTextField
                id={`${id}-set-client-subsystems`}
                label={t(TK.subSystems)}
                helpText={t(TK.subSystemsHelpNote)}
                multiSelect
                options={values.clientSubsystems}
                values={values.clientSubsystems}
                onAddNewOption={value => onChange({...values, clientSubsystems: [...values.clientSubsystems, value]})}
                onValuesChanged={clientSubsystems => onChange({...values, clientSubsystems})}
            />

            <div>
                <AutocompleteTextField
                    id={`${id}-set-client-tags`}
                    label={t(TK.clientTags)}
                    helpText={t(TK.clientTagsHelpNote)}
                    multiSelect
                    options={Object.keys(values.clientTags ?? {})}
                    values={Object.keys(values.clientTags ?? {})}
                    onAddNewOption={value => onChange({...values, clientTags: {...values.clientTags, [value]: {color: '#AAA'}}})}
                    onValuesChanged={clientTags => onChange({...values, clientTags: Object.fromEntries(clientTags.map(key => [key, values.clientTags[key] ?? {color: '#AAA'}]))})}
                />
                <CollapsableItem title={t(TK.advanced)} size='sm' style={{paddingLeft: '1rem'}}>
                    <div style={{paddingBottom: '2rem'}}>
                        <div style={{marginBottom: 'var(--spacer-S)'}}>
                            <ClientsIntelligentCategorizationSettingsLink />
                        </div>
                        <Table responsive size="sm">
                            <thead>
                                <th></th>
                            </thead>
                            <tbody>
                                {Object.entries(values.clientTags ?? {}).map(([tag, tagSettings]) => (
                                    <tr key={tag}>
                                        <td>
                                            <ColorBall 
                                                color={tagSettings.color} 
                                                onChange={color => onChange({...values, clientTags: {...values.clientTags, [tag]: {color}}})}
                                            >
                                                {tag}
                                            </ColorBall>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </CollapsableItem>
            </div>

        </Form>
    );
}

export default GeneralSettings;
